import { gql } from '@apollo/client'
import {
  BodyText,
  ChevronRightIcon,
  Chip,
  Collapse,
  Heading,
  Information,
  Spinner,
  Timeline,
} from '@farol-ds/react'
import { LoginTrigger } from '@jusbrasil-web/shared-bifrost'
import { useBoxModal as useBoxLock } from '@jusbrasil-web/shared-box-lock'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  COMPONENT_NAME,
  FEATURE_CLASS,
  FEATURE_NAME,
  GA4ClickEvent,
  GA4CustomComponentRenderedEvent,
  GA4FeatureUseEvent,
  GA4FeatureUseEventProps,
  REQUIRED_USER_STATUS,
} from '@jusbrasil-web/shared-ga4-events'
import { useValidateOabModal } from '@jusbrasil-web/shared-validate-oab-modal'
import classNames from 'classnames'
import Link from 'next/link'
import { MouseEvent, ReactNode, useEffect, useMemo, useRef } from 'react'

import { FEATURE_CODES } from '../../constants'
import { useBoxModal, useMetrics, useModalContext } from '../../hooks'
import widgetStyles from '../../widget-common.module.scss'
import {
  RelatedDocuments_DocumentFragment,
  RelatedDocuments_ViewerFragment,
} from './__generated__/related-documents.graphql'
import { getRelatedDocs, RelatedDoc } from './functions'
import styles from './related-documents.module.scss'

export interface RelatedDocumentsProps {
  document: RelatedDocuments_DocumentFragment
  viewer: RelatedDocuments_ViewerFragment | null
  enableOabValidation?: boolean
}

const INITIAL_VISIBLE_DOCS = 5
const boxProps = {
  featureCode: FEATURE_CODES.LINK_DOCS_PROCESSO,
  fromComponent: 'docview/RelatedDocuments',
}

export function RelatedDocuments(props: RelatedDocumentsProps) {
  const { document, viewer, enableOabValidation = false } = props
  const relatedDocs = useMemo(() => getRelatedDocs(document), [document])
  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()
  const { sendGA4Event } = useGA4Dispatcher()
  const loadingDocIdsRef = useRef<number[]>([])
  const boxLockProps = {
    appId: 'web-docview',
    featureCode: boxProps.featureCode,
    fromComponent: boxProps.fromComponent,
    boxOptions: {},
    pageMetadata: {},
  }
  const { setOpen, initialized: boxLockInitialized } = useBoxLock({ ...boxLockProps, viewer })
  const boxModal = useBoxModal(boxProps)
  const { insideModal } = useModalContext()

  const validateOabModal = useValidateOabModal<HTMLAnchorElement>({ origin: 'LOCK_OAB_MOTION' })
  const shouldValidateOab = () =>
    enableOabValidation && !!viewer && !(viewer.isLawyer && viewer.isVerified)

  const shoulRenderComponent = () => {
    return relatedDocs.length > 1 || (relatedDocs.length === 1 && !relatedDocs[0].isCurrentDoc)
  }

  useEffect(() => {
    if (shoulRenderComponent()) {
      sendGA4Event(
        new GA4CustomComponentRenderedEvent({
          componentClass: COMPONENT_CLASS.LAWSUIT_DOCUMENTS,
          componentName: insideModal
            ? COMPONENT_NAME.JURIS_SHOW_MORE_DETAILS
            : COMPONENT_NAME.SIDE_CONTENT,
          resourceId: document.boxResourceId,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!shoulRenderComponent()) {
    return null
  }

  const sendRelatedDocAccessAttemptGA4Event = (
    doc: RelatedDoc,
    extraParameters: Partial<GA4FeatureUseEventProps> = {}
  ) => {
    sendGA4Event(
      new GA4FeatureUseEvent({
        componentClass: COMPONENT_CLASS.LAWSUIT_DOCUMENTS,
        componentName: insideModal
          ? COMPONENT_NAME.JURIS_SHOW_MORE_DETAILS
          : COMPONENT_NAME.SIDE_CONTENT,
        featureClass: FEATURE_CLASS.UNLOCK_DOCUMENT_FILES,
        featureName: FEATURE_NAME.LAWSUIT_DOCS_LINK,
        resourceId: `${doc.artifact}:${doc.docId}`,
        requiredUserStatus: REQUIRED_USER_STATUS.SUBSCRIBER_USER,
        selectedOption: `LAWSUIT_${doc.artifact}`,
        ...extraParameters,
      })
    )
  }

  const handleClick = async (event: MouseEvent, doc: RelatedDoc) => {
    event.preventDefault()

    if (boxModal.loading || !boxLockInitialized) {
      loadingDocIdsRef.current.push(doc.docId)
    }

    if (!viewer) {
      sendRelatedDocAccessAttemptGA4Event(doc, { successfulAttempt: false })
      return
    }

    if (!document.viewerCanAccessDocumentLinks) {
      setOpen(true)
      sendRelatedDocAccessAttemptGA4Event(doc, { successfulAttempt: false })
      return
    }

    sendRelatedDocAccessAttemptGA4Event(doc, { successfulAttempt: true })

    sendMetrics('Doc.RelatedDocumentLinkClicked', {
      from_component: boxProps.fromComponent,
      metadata: {
        relatedDocumentArtifactClicked: doc.artifact,
        relatedDocumentLabelClicked: doc.label,
      },
    })

    if (shouldValidateOab()) {
      validateOabModal.setOpen(true)
    } else {
      window.open(doc.url, '_blank')
    }
  }

  const renderItem = (doc: RelatedDoc) => {
    const Wrapper = ({ children }: { children: ReactNode }) =>
      doc.isCurrentDoc ? (
        <div className={styles.itemWrapper}>{children}</div>
      ) : (
        <LoginTrigger
          isLogged={!!viewer}
          options={{
            eventData: {
              feature_code: boxProps.featureCode,
              from_component: boxProps.fromComponent,
            },
          }}
        >
          <Link
            href={doc.url}
            onClick={(ev) => handleClick(ev, doc)}
            ref={validateOabModal.metricsRef}
            className={classNames(styles.itemWrapper, styles.link)}
            target="_blank"
          >
            {children}
          </Link>
        </LoginTrigger>
      )

    return (
      <Wrapper>
        <div className={styles.textWrapper}>
          <BodyText
            className={classNames(styles.itemTitle, {
              [styles.currentDocText]: doc.isCurrentDoc,
            })}
          >
            <strong>{doc.label}</strong>
          </BodyText>
          <BodyText className={classNames({ [styles.currentDocText]: doc.isCurrentDoc })}>
            {doc.publisher} - {doc.formattedDate}
          </BodyText>
        </div>

        {doc.isCurrentDoc ? (
          <Chip kind="secondary" size="sm">
            Atual
          </Chip>
        ) : (
          <>
            {(boxModal.loading || !boxLockInitialized) &&
            loadingDocIdsRef.current.includes(doc.docId) ? (
              <Spinner size="sm" />
            ) : (
              <ChevronRightIcon className={styles.icon} />
            )}
          </>
        )}
      </Wrapper>
    )
  }

  const renderTimelineSlice = (start?: number, end?: number) =>
    relatedDocs
      .slice(start, end)
      .map((doc) => <Timeline.Item key={doc.docId}>{renderItem(doc)}</Timeline.Item>)

  const renderTimeline = () => {
    const shouldRenderCollapse = relatedDocs.length > INITIAL_VISIBLE_DOCS
    const timelineItems = renderTimelineSlice(0, INITIAL_VISIBLE_DOCS)

    if (shouldRenderCollapse) {
      return (
        <Collapse onOpenChange={handleCollapseOpenChange}>
          <Timeline>
            {timelineItems}
            <Collapse.Content>{renderTimelineSlice(INITIAL_VISIBLE_DOCS)}</Collapse.Content>
          </Timeline>
        </Collapse>
      )
    }

    return <Timeline>{timelineItems}</Timeline>
  }

  const handleCollapseOpenChange = (open: boolean) => {
    if (!open) return

    sendGA4Event(
      new GA4ClickEvent({
        componentClass: COMPONENT_CLASS.LAWSUIT_DOCUMENTS,
        componentName: insideModal
          ? COMPONENT_NAME.JURIS_SHOW_MORE_DETAILS
          : COMPONENT_NAME.SIDE_CONTENT,
        resourceId: document.boxResourceId,
      })
    )
  }

  return (
    <div>
      <div className={styles.headingWithInfo}>
        <Heading size="md" asChild>
          <h2 className={widgetStyles.cardTitle}>Documentos do processo</h2>
        </Heading>
        <Information
          label="O acesso a esses documentos é disponibilizado apenas para assinantes do Jusbrasil."
          size="xs"
        />
      </div>

      <div className={widgetStyles.cardContent} ref={dispatcherRef}>
        {renderTimeline()}
      </div>
    </div>
  )
}

RelatedDocuments.fragments = {
  document: gql`
    fragment RelatedDocuments_document on Document {
      docId
      boxResourceId
      viewerCanAccessDocumentLinks: viewerCanUseFeature(featureCode: "link_docs_processo")
      parentDocument {
        docId
      }
      relatedJuris @ignoreExceptions {
        docId
        artifact
        jurisTypeLabel
        url
        date
        publisher {
          acronym
        }
      }
      relatedMotions @ignoreExceptions {
        docId
        artifact
        motionTypeLabel
        url
        courtPublishingDate
        publisher {
          acronym
        }
      }
    }
  `,
  viewer: gql`
    fragment RelatedDocuments_viewer on Profile {
      isLawyer
      isVerified

      ...useBoxModal_viewer
    }
    ${useBoxLock.fragments.viewer}
  `,
}
